import {match} from 'ts-pattern';
import {z} from 'zod';

import {isNil} from 'ramda';

import {parseDate} from 'shared/web';

import type {GridPdfExportContext} from 'features/eag-grid';
import {PdfExportError} from 'features/pdf-export';

import {TIRE_SET_PDF_COLUMNS} from '../constants/TIRE_SET_PDF_COLUMNS';

const EMPTY_VALUE = '-';
const textCellSchema = z.object({value: z.string().nullable()});
const labelCellSchema = z.object({value: z.object({label: z.string().nullable()}).nullable()});
const countCellSchema = z.object({value: z.number().int().nonnegative().nullable()});
const isoDateSchema = z.union([z.string().date(), z.string().datetime({offset: true})]);

function formatDateCell(cell: unknown, format: (value: Date) => string) {
  const value = textCellSchema.parse(cell).value;
  if (isNil(value) || value.trim().length === 0) {
    return null;
  }
  const parsed = parseDate(isoDateSchema.parse(value));
  if (Number.isNaN(parsed.getTime())) {
    throw new RangeError('Invalid tire-set PDF date');
  }
  return format(parsed);
}

/** Use the grid's selected snapshot and localized titles, including hidden PDF columns. */
export function createTireSetPdfData(
  context: GridPdfExportContext,
  format: {
    date: (value: Date) => string;
    dateTime: (value: Date) => string;
    integer: (value: number) => string | null;
  }
) {
  if (context.rows.length === 0) {
    throw new PdfExportError('general.notifications.noItemsSelected');
  }

  const titles = new Map(context.columns.map((column) => [column.key, column.title]));
  return context.rows.map((row) => {
    const field = (key: (typeof TIRE_SET_PDF_COLUMNS)[number]) => {
      try {
        const label = titles.get(key);
        if (isNil(label) || label.trim().length === 0) {
          throw new Error(`Missing tire-set PDF column: ${key}`);
        }
        const cell = row.cells[key];
        const value = match(key)
          .with('setStoreUntil', () => formatDateCell(cell, format.date))
          .with('neededAt', () => formatDateCell(cell, format.dateTime))
          .with('wheelNumber', () => {
            const count = countCellSchema.parse(cell).value;
            if (isNil(count)) {
              return null;
            }
            const formatted = format.integer(count);
            if (isNil(formatted) || formatted.trim().length === 0) {
              throw new Error('Active tire count could not be formatted');
            }
            return formatted;
          })
          .with(
            'branch',
            'storageWarehouse',
            'vehicle',
            'tireCategory',
            'requestType',
            'requestState',
            'requestHomeBranch',
            'requestDestinationBranch',
            () => labelCellSchema.parse(cell).value?.label
          )
          .otherwise(() => textCellSchema.parse(cell).value);
        return {
          key,
          label,
          value: isNil(value) || value.trim().length === 0 ? EMPTY_VALUE : value,
        };
      } catch (cause) {
        throw new PdfExportError(
          'module.reportDocument.labels.dataUnavailable',
          false,
          'entity.integration.labels.pleaseRefresh',
          {cause}
        );
      }
    };

    return {
      id: row.id,
      setNumber: field('setNumber'),
      branch: field('branch'),
      storageWarehouse: field('storageWarehouse'),
      storageLocation: field('storageLocation'),
      wheelNumber: field('wheelNumber'),
      setStoreUntil: field('setStoreUntil'),
      vehicle: field('vehicle'),
      vehicleRegistrationPlate: field('vehicleRegistrationPlate'),
      vehicleVin: field('vehicleVin'),
      contactPersonName: field('contactPersonName'),
      contactPersonPhone: field('contactPersonPhone'),
      tireCategory: field('tireCategory'),
      tireWheel: field('tireWheel'),
      tireSeason: field('tireSeason'),
      dot: field('dot'),
      requestType: field('requestType'),
      requestState: field('requestState'),
      neededAt: field('neededAt'),
      requestHomeBranch: field('requestHomeBranch'),
      requestDestinationBranch: field('requestDestinationBranch'),
    } satisfies Record<
      (typeof TIRE_SET_PDF_COLUMNS)[number],
      {key: string; label: string; value: string}
    > & {id: string};
  });
}
